import React, { useState, useEffect } from 'react';
import { X, Sun, Moon, Monitor, User, Save, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { updateProfile } from '../api/api';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const SettingsModal = ({ isOpen, onClose, brandVisualTheme, user, onUpdateUser }) => {
  const { theme, changeTheme } = useTheme();
  const [name, setName] = useState('');
  const [image, setImage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  const accent = brandVisualTheme?.palette?.primary ?? '#1E8997';
  const accentDark = brandVisualTheme?.palette?.dark ?? '#0e273a';
  const brandThemeName = brandVisualTheme?.brandName ?? 'NodeRiver';

  useEffect(() => {
    if (isOpen && user) {
        setName(user.name || '');
        setImage(user.image || '');
    }
  }, [isOpen, user]);

  const handleSaveProfile = async () => {
      if (!user) return;
      setIsSaving(true);
      try {
          await updateProfile({ name, image });
          if (onUpdateUser) {
             onUpdateUser({ ...user, name, image });
          }
          // Maybe show a toast?
      } catch (e) {
          console.error("Failed to update profile", e);
      } finally {
          setIsSaving(false);
      }
  };

  if (!isOpen) return null;

  const NodeRiverIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" fill="url(#noderiver-gradient)" />
      <defs>
        <linearGradient id="noderiver-gradient" x1="12" y1="2" x2="12" y2="22">
          <stop offset="0%" stopColor={accent} />
          <stop offset="100%" stopColor={accentDark} />
        </linearGradient>
      </defs>
    </svg>
  );

  const themes = [
    { id: 'light', name: 'Light', icon: Sun, description: 'Clean and bright' },
    { id: 'dark', name: 'Dark', icon: Moon, description: 'Easy on the eyes' },
    { id: 'noderiver', name: brandThemeName, icon: NodeRiverIcon, description: `${brandThemeName} accent` },
    { id: 'auto', name: 'Auto', icon: Monitor, description: 'Follows system' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          
          {user && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Profile</h3>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16 border border-gray-200 dark:border-gray-700">
                    {image ? <AvatarImage src={image} /> : null}
                    <AvatarFallback className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-lg">
                      {name ? name.charAt(0).toUpperCase() : <User className="w-8 h-8" />}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                     <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                       Profile Image URL
                     </label>
                     <input
                       type="text"
                       value={image}
                       onChange={(e) => setImage(e.target.value)}
                       placeholder="https://..."
                       className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                     />
                  </div>
                </div>
                
                <div>
                   <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                     Display Name
                   </label>
                   <input
                     type="text"
                     value={name}
                     onChange={(e) => setName(e.target.value)}
                     placeholder="Your name"
                     className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                   />
                </div>

                <button
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Appearance</h3>
            <div className="space-y-2">
              {themes.map((themeOption) => {
                const Icon = themeOption.icon;
                const isSelected = theme === themeOption.id;
                
                return (
                <button
                  key={themeOption.id}
                  onClick={() => changeTheme(themeOption.id)}
                  className={`w-full flex items-center space-x-3 p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-transparent'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  style={
                    isSelected
                      ? { borderColor: accent, backgroundColor: `${accent}1a` }
                      : undefined
                  }
                >
                    <div className={`p-2 rounded-lg ${
                      isSelected 
                      ? 'text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}
                  style={isSelected ? { background: accent } : undefined}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${
                        isSelected 
                          ? 'text-gray-900 dark:text-white' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {themeOption.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {themeOption.description}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-900 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:opacity-90 transition-opacity font-medium"
          >  
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
